import React from 'react';
import { motion } from 'framer-motion';

// Helper function to highlight matching letters
const highlightMatches = (text, query) => {
  if (!query || !text) return text;
  
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, index) => 
    regex.test(part) ? (
      <span key={index} className="bg-brand/30 text-brand font-medium">
        {part}
      </span>
    ) : part
  );
};

export default function CustomerList({ customers, onSelect, searchQuery = '' }) {
  // Filter customers based on search query
  const filteredCustomers = customers.filter(client => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.company?.toLowerCase().includes(query)
    );
  });

  if (!customers || customers.length === 0) {
    return (
      <div className="p-4 text-center">
        <div className="flex items-center justify-center w-10 h-10 mx-auto mb-3 bg-gray-100 dark:bg-[#1A1A1A] rounded-full">
          <svg className="w-5 h-5 text-gray-400 dark:text-[#666666]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <p className="text-sm text-gray-500 dark:text-[#666666]">No clients found</p>
        <p className="text-xs text-gray-400 dark:text-[#666666] mt-1">Add a new client to get started</p>
      </div>
    );
  }

  // If no matches after filtering, don't render anything - parent will handle showing new client form
  if (filteredCustomers.length === 0) {
    return null;
  }

  return (
    <div className="py-2 max-h-60 overflow-y-auto">
      {filteredCustomers.map((client, index) => (
        <motion.div
          key={client.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.03 }}
          whileHover={{ x: 4 }}
          onClick={() => onSelect(client)}
          className="group relative mx-2 mb-1 last:mb-0 px-4 py-3 rounded-xl text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-[#1A1A1A] cursor-pointer transition-all duration-200"
        >
          <div className="flex items-center space-x-3">
            {/* Avatar */}
            <div className="w-8 h-8 bg-gradient-to-br from-brand to-[#FF9D5C] rounded-full flex items-center justify-center text-sm font-medium text-black flex-shrink-0">
              {client.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            
            {/* Client Info */}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                {highlightMatches(client.name, searchQuery)}
              </div>
              {client.email && (
                <div className="text-xs text-gray-500 dark:text-[#666666] truncate">
                  {highlightMatches(client.email, searchQuery)}
                </div>
              )}
              {client.company && (
                <div className="text-xs text-gray-400 dark:text-[#666666] truncate">
                  {highlightMatches(client.company, searchQuery)}
                </div>
              )}
            </div>
            
            {/* Arrow indicator */}
            <svg className="w-4 h-4 text-gray-400 dark:text-[#666666] group-hover:text-brand transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
          
          {/* Hover indicator */}
          <div className="absolute inset-0 rounded-xl border border-transparent group-hover:border-brand/20 transition-colors pointer-events-none" />
        </motion.div>
      ))}
    </div>
  );
}